import { Component } from '@theme/component';
import { prefersReducedMotion, requestIdleCallback } from '@theme/utilities';

/**
 * New Arrivals split slider (sections/carousel-products.liquid).
 *
 * Each _carousel-products-slide block renders its pieces — heading, media
 * (editorial image) and product grid — as tagged siblings in the blocks
 * container, because theme-block settings only resolve inside the block's
 * own render. On init this component slots the headings and media into
 * their display containers, pairing pieces by data-block-id.
 *
 * Interaction (Figma annotation on 6084:18796): the LEFT image slides
 * horizontally; the RIGHT panel is static — the heading and product grid
 * belonging to the incoming image swap in place. The chevrons at the outer
 * edges resolve on:click="/previous|/next" to this component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} headings - Container the headings are slotted into.
 * @property {HTMLElement} mediaCol - Image viewport the media layers are slotted into.
 * @property {HTMLElement} blocks - Blocks container (keeps the grids).
 * @extends {Component<Refs>}
 */
class NaSplitSlider extends Component {
  requiredRefs = ['headings', 'mediaCol', 'blocks'];

  /** @type {{grid: Element, media: Element | null, heading: Element | null}[]} */
  #slides = [];

  #current = 0;

  #animating = false;

  connectedCallback() {
    super.connectedCallback();

    this.#slot();

    if (window.Shopify?.designMode) {
      this.addEventListener('shopify:block:select', this.#onBlockSelect);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('shopify:block:select', this.#onBlockSelect);
  }

  /**
   * Move each slide's heading and media out of the blocks container into
   * their display containers, in block order, then show slide 0.
   */
  #slot() {
    this.#slides = [...this.refs.blocks.querySelectorAll('.na-split__grid')].map((grid) => {
      const blockId = grid.getAttribute('data-block-id');
      const media = this.refs.blocks.querySelector(`.na-split__media[data-block-id="${blockId}"]`);
      const heading = this.refs.blocks.querySelector(`.na-split__heading[data-block-id="${blockId}"]`);

      if (media) this.refs.mediaCol.append(media);
      if (heading) this.refs.headings.append(heading);

      return { grid, media, heading };
    });

    this.#current = 0;
    this.#activate(0);
    this.classList.add('is-ready');

    // Lazy images inside display:none grids never intersect, so they'd only
    // start fetching when their slide is first shown — a visible pop-in.
    // Warm every hidden slide's images once the browser is idle.
    requestIdleCallback(() => {
      for (const img of this.querySelectorAll(
        '.na-split__grid img[loading="lazy"], .na-split__media img[loading="lazy"]',
      )) {
        img.loading = 'eager';
      }
    });
  }

  /** @param {Event} event */
  previous(event) {
    event?.preventDefault();
    this.#goTo(this.#current - 1, -1);
  }

  /** @param {Event} event */
  next(event) {
    event?.preventDefault();
    this.#goTo(this.#current + 1, 1);
  }

  /**
   * Swap heading + grid in place and slide the media layer in from the
   * travel direction.
   * @param {number} index - Target slide index (wraps).
   * @param {number} direction - 1 = forwards, -1 = backwards.
   */
  #goTo(index, direction) {
    const count = this.#slides.length;
    if (count < 2 || this.#animating) return;

    const target = ((index % count) + count) % count;
    if (target === this.#current) return;

    const from = this.#slides[this.#current];
    const to = this.#slides[target];

    this.#activate(target);
    this.#current = target;

    if (prefersReducedMotion() || !from.media?.animate || !to.media) return;

    // Slide the incoming layer in from the travel side while the outgoing
    // one leaves the other way. Web Animations API: composited, no rAF.
    this.#animating = true;
    const incoming = to.media;
    const outgoing = from.media;
    const options = { duration: 450, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' };

    incoming.classList.add('is-active');
    outgoing.classList.add('is-active');

    const slideIn = incoming.animate(
      [{ transform: `translateX(${direction * 100}%)` }, { transform: 'translateX(0)' }],
      options,
    );
    const slideOut = outgoing.animate(
      [{ transform: 'translateX(0)' }, { transform: `translateX(${direction * -100}%)` }],
      { ...options, fill: 'forwards' },
    );

    // Settle via the animations when possible, but never depend on them —
    // browsers throttle animation clocks for occluded/offscreen content.
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      outgoing.classList.remove('is-active');
      slideOut.cancel();
      slideIn.cancel();
      this.#animating = false;
    };
    Promise.allSettled([slideIn.finished, slideOut.finished]).then(finish);
    setTimeout(finish, 600);
  }

  /** Mark the pieces of the given slide as active. */
  #activate(index) {
    this.#slides.forEach(({ grid, media, heading }, slideIndex) => {
      const active = slideIndex === index;
      grid.classList.toggle('is-active', active);
      media?.classList.toggle('is-active', active);
      heading?.classList.toggle('is-active', active);
    });
  }

  /**
   * Theme editor: selecting a Slide block (its root is the grid) or a
   * Product block nested inside it brings that slide into view.
   * @param {Event} event
   */
  #onBlockSelect = (event) => {
    const grid = /** @type {Element} */ (event.target).closest('.na-split__grid');
    if (!grid) return;

    const index = this.#slides.findIndex((slide) => slide.grid === grid);
    if (index >= 0 && index !== this.#current) {
      this.#goTo(index, index > this.#current ? 1 : -1);
    }
  };
}

if (!customElements.get('na-split-slider')) {
  customElements.define('na-split-slider', NaSplitSlider);
}
